 
import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  AppButton,
  Box, CardTypesEnum, CheckBox, Hint, Preloader, Text } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import PlanCard from '@/components/elements/PlanCard';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { useCurrentOrganization } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { usePlanReactivationAccess } from '@/modules/plan/hooks/usePlanReactivationAccess';
import ModuleWrapper from '@/modules/plan/ModuleWrapper';

import { PLAN_MODULES } from '../../plan.constants';
import { PlanCardMode, PlanFieldState, PlanStatuses, PlansType } from '../../plan.types';
import { PlanReactivationModal, usePlanReactivationModal } from '../../PlanReactivation';
import { CancelPlanTerminationModal } from '../../PlanTermination/CancelPlanTerminationModal';
import { CancelPlanTerminationSuccessModal } from '../../PlanTermination/CancelPlanTerminationSuccessModal';
import { useTerminatePlan } from '../../PlanTermination/hooks';
import usePlanTerminationBanner from '../../PlanTermination/hooks/usePlanTerminationBanner';
import { PlanTerminationModal } from '../../PlanTermination/PlanTerminationModal';
import { useCancelPlanTermination } from '../../PlanTermination/PlanTerminationModalForms/hooks/useCancelPlanTermination';
import { PlanTerminationSuccessModal } from '../../PlanTermination/PlanTerminationSuccessModal';
import { useCurrentPlan } from '../hooks';
import { usePreviewStore,useSkipConfirmMode } from '../stores';

import { useDeletePlanQuery } from './queries';
import { ReactivationDangerZoneCard } from './ReactivationDangerZoneCard';

interface Props {
  viewMode?: boolean;
  isTemplateMode?: boolean;
}
const DELAY_TIME = 2000;
const DangerZone: React.FC<Props> = ({ isTemplateMode }) => {
  const previewMode = usePreviewStore((state) => state.previewMode);
  const setSkipConfirmMode = useSkipConfirmMode((state) => state.setSkipConfirm);
  const history = useHistory();
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: plan } = useCurrentPlan();
  const isDisabled = useMemo(() => !plan?.deletable, [plan]);
  const {
    handleDelete,
  } = useDeletePlanQuery(`${plan?.id}`);
  const hasAccessTerminateEmployer = useHasAccess([
    {
      permission: Permissions.PLAN_SETUP,
    },
  ]);

  const planTerminationAccess = useMemo(() => {
    if (!plan) return false;
    return !isTemplateMode
    && hasAccessTerminateEmployer;
  }
  , [plan, isTemplateMode, hasAccessTerminateEmployer]);
  const isPlanReadyForTermination = useMemo(() => {
    if (!plan) return false;
    return plan.plan_status === PlanStatuses.ACTIVE
    && planTerminationAccess;
  }, [plan, planTerminationAccess]);

  const isPlanPendingTermination = useMemo(() => {
    if (!plan) return false;
    return plan.plan_status === PlanStatuses.PENDING_TERMINATION
    && planTerminationAccess;
  }, [plan, planTerminationAccess]);

  const { hasAccessReactivatePlan } = usePlanReactivationAccess();

  const isPlanReadyForReactivation = useMemo(() => {
    if (!plan || isTemplateMode || !hasAccessReactivatePlan) return false;
    return plan.plan_status === PlanStatuses.TERMINATED
      || plan.plan_status === PlanStatuses.TERMINATION_IN_PROGRESS
      || plan.plan_status === PlanStatuses.PENDING_REACTIVATION;
  }, [plan, isTemplateMode, hasAccessReactivatePlan]);

  const { openPlanTerminationModal, handleOpenEditPlanTerminationModal } = useTerminatePlan();
  const { handleOpenCancelPlanTerminationModal } = useCancelPlanTermination();
  const { terminationDate } = usePlanTerminationBanner();
  const { showBanner: isOrgTermination } = useEmployerTerminationBanner();

  const { openSingleReactivation } = usePlanReactivationModal();

  const handleReactivatePlan = useCallback(() => {
    openSingleReactivation(`${plan?.id}`, plan?.name?.name, () => location.reload());
  }, [plan, openSingleReactivation]);

  const { observingOrganization } = useCurrentOrganization();
  const isTerminationStatus = useMemo(() => observingOrganization.status === EmployerStatus.PendingTermination
  || observingOrganization.status === EmployerStatus.TerminationInProgress
  || observingOrganization.status === EmployerStatus.Terminated, [observingOrganization]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setShowModal(false);
      setLoading(false);
      setConfirmed(false);
      setSubmitted(false);
    }
  }, [loading]);

  const handleConfirm = () => {
    setSubmitted(true);
    if (confirmed) {
      setLoading(true);

      setTimeout(async () => {
        try {
          setShowModal(false);
          await handleDelete();
          setSuccessModal(true);
          setSkipConfirmMode(true);
        } catch {
          setLoading(false);
        }
      }, DELAY_TIME);
    }
  };
  const handleOpenPlans = useCallback(() => {
    if (isTemplateMode) {
      history.originPush(`${history.getPath(ROUTES.PLANS)}#${PlansType.Template}`);
    } else {
      history.originPush(`${history.getPath(ROUTES.PLANS)}#${PlansType.Plan}`);
    }
  }, [history, isTemplateMode]);


  return (
    <>
      <WarnModal
        visible={successModal}
        isSuccessIcon
        onSetVisible={(value: boolean) => (value ? setSuccessModal(value) : handleOpenPlans())}
        header={isTemplateMode ? `${plan?.name?.name} plan template was deleted` : `${plan?.name?.name} was deleted`}
        buttonText=""
        cancelButtonNode={(
          <AppButton
            testId="go-to-plans"
            onClick={handleOpenPlans}
          >
            {isTemplateMode ? 'Go to Plan Templates' : 'Go to Plans'}
          </AppButton>
        )}
      />
      <PlanTerminationModal />
      <PlanTerminationSuccessModal />
      <CancelPlanTerminationModal />
      <CancelPlanTerminationSuccessModal />
      <PlanReactivationModal />
      {showModal && (
        <WarnModal
          isPendingIcon
          visible
          paddingless
          onSetVisible={(value: boolean) => (value ? setShowModal(true) : handleClose())}
          onCancel={handleClose}
          header={` Delete ${plan?.name.name} ?`}
          buttonText="Cancel"
          onSubmit={handleConfirm}
          submitButtonMode="alert"
          cancelButtonNode={(

            <Box direction="row">
              <Box width="small" margin={{ right: 'spacing6' }}>
                <AppButton
                  buttonType="secondary"
                  onClick={handleClose}
                  width="100%"
                >
                  Cancel
                </AppButton>
              </Box>
              <Box width={{ min: 'small' }} margin={{ left: 'spacing6' }}>
                <AppButton
                  testId="accept-delete-plan"
                  color="red"
                  onClick={handleConfirm}
                  width="100%"
                >
                  {loading ? <Preloader color="white" /> : (
                    isTemplateMode
                      ? 'Yes, Delete This Plan Template'
                      : 'Yes, Delete This Plan'
                  )}
                </AppButton>
              </Box>
            </Box>

          )}

        >
          <Box fill direction="column">
            <Box
              background="module"
              fill
              justify="center"
              align="center"
              round="moduleRound"
              margin={{ vertical: 'spacing24' }}
              pad={{ vertical: 'spacing12' }}

            >
              <Box align='center' style={{position:'relative'}}>
                <PlanCard
                  plan={{
                    id: `${plan?.id}`,
                    name: plan?.name?.name || '',
                    typeState: PlanFieldState.Modifiable,
                    type: plan?.account_type.account_type as CardTypesEnum,
                    status: plan?.plan_status as PlanStatuses,
                    info: {},
                  }}
                  isFlat
                  isTemplateMode={isTemplateMode}
                  mode={PlanCardMode.Plan}
                  // style={{
                  //   width: theme.controls.formWidth.width,
                  //   minHeight: theme.controls.cardHeight,
                  // }}
                />
              </Box>
            </Box>
            <Box direction="row" align="center" justify="center">
              <CheckBox
                name="primary"
                role="checkbox"
                data-testid="delete-plan-checkbox"
                aria-label="primary"
                checked={confirmed}
                onChange={({ target }) => setConfirmed(target.checked)}
                label={'I understand that deleting is permanent, and can\'t be undone'}
              />
            </Box>
            {submitted && !confirmed && (
              <Box direction="row" data-testid="accept-delete-plan-error" align="center" justify="center">
                <Text color="danger">
                  Confirm you agree, that deleting is permanent
                </Text>
              </Box>
            )}
          </Box>
        </WarnModal>
      )}

      <ModuleWrapper
        anchorId={PLAN_MODULES.DANGER_ZONE}
        title="Danger Zone"
        expandless
      >
        <Box direction="row">

          {!isPlanReadyForReactivation && (
            <Box
              fill
              align="center"
              pad="spacing24"
              round="container1Round"
              direction="column"
              background="canvas"
              margin={{ right: 'spacing8' }}
            >
              <Text size="2xl" color="textBody" weight={700}>
                Delete
              </Text>
              <Box fill margin={{ top: 'spacing24' }} direction="column">
                <Hint
                  hideHint={!isTerminationStatus || isTemplateMode}
                  hintAlign="left"
                  hintElement={(
                    <AppButton
                      testId="delete-plan"
                      color="red"
                      size="L"
                      disabled={isDisabled || previewMode || (isTerminationStatus && !isTemplateMode)}
                      onClick={() => setShowModal(true)}
                      width="100%"
                    >
                      {isTemplateMode ? 'Delete Plan Template' : 'Delete Plan'}
                    </AppButton>
                  )}
                >
                  <Text size="small" color="textOnColor" textAlign="center">
                    Terminated organizations cannot delete plan.
                  </Text>
                </Hint>
                {isDisabled
                && (
                  <Box fill align="center" margin={{ top: 'spacing24' }} justify="center">
                    <Text
                      textAlign="center"
                      color="textSecondary"
                    >
                      {isTemplateMode
                        ? 'This plan template cannot be deleted because it is connected to another template, plan, or payment order.'
                        : 'This plan cannot be deleted because it has the enrollments.'}

                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {isPlanReadyForReactivation ? (
            <ReactivationDangerZoneCard
              planId={plan?.id}
              planStatus={plan?.plan_status}
              onReactivatePlan={handleReactivatePlan}
              planName={plan?.name?.name}
            />
          ) : (isPlanReadyForTermination || isPlanPendingTermination) ? (
          <Box
            data-testid="terminate-plan"
            fill
            align="center"
            pad="spacing24"
            round="container1Round"
            background="canvas"
            margin={{ right: 'spacing8' }}
          >
            <Text size="2xl" color="textBody" weight={700}>
              Terminate
            </Text>
            <Box
              fill
              margin={{ top: 'spacing24' }}
              direction='column'
            >

              {isPlanPendingTermination ? (
                <Box width="100%" direction="row" gap="spacing12">
                  <Box fill>
                    <AppButton
                      testId="cancel-termination"
                      buttonType="secondary"
                      size="L"
                      onClick={() => handleOpenEditPlanTerminationModal(
                        `${plan?.id}`,
                        terminationDate,
                        plan?.name?.name,
                        plan?.account_type.account_type?.includes(CardTypesEnum.HSA)
                      )}
                      width="100%"
                    >
                      Edit Termination
                    </AppButton>
                  </Box>
                  <Box fill>
                    <AppButton
                      testId="cancel-termination"
                      color="red"
                      buttonType="secondary"
                      disabled={isOrgTermination}
                      size="L"
                      onClick={() => handleOpenCancelPlanTerminationModal(
                        `${plan?.id}`,
                        plan?.name?.name,
                        plan?.account_type.account_type?.includes(CardTypesEnum.HSA)
                      )}
                      width="100%"
                    >
                      Cancel Termination
                    </AppButton>
                  </Box>
                </Box>
                ) : (
                <AppButton
                  testId="terminate-plan"
                  color="red"
                  size="L"
                  disabled={previewMode}
                  onClick={() => openPlanTerminationModal(
                    `${plan?.id}`,
                    plan?.name?.name,
                    plan?.account_type.account_type?.includes(CardTypesEnum.HSA),
                    plan?.plan_year?.valid_from,
                  )}
                  width="100%"
              >
                Terminate Plan
                </AppButton>
              )}
            </Box>
          </Box>
          ) : (
          <Box fill margin={{ left: 'spacing8' }} />
        )}
        </Box>

      </ModuleWrapper>

    </>
  );
};

export default DangerZone;
